<script setup>
import { computed } from 'vue';
import { useReportFormatter } from 'dashboard/composables/useReportFormatter';
import HeatmapTooltip from './heatmaps/HeatmapTooltip.vue';
import { useHeatmapTooltip } from './heatmaps/composables/useHeatmapTooltip';

// A grade de dia da semana × hora dos mapas de calor (Campanhas e Números), no
// fuso que a resposta diz. A linha é o dia da semana (`dow` 0 é domingo), e não
// a data, como no `BaseHeatmap`; dele, só o tooltip. Quem usa diz o valor e o
// texto de cada casa; a frase de quando não há nada fica com ele. 24 horas
// precisam de 700 px: o quadro rola sozinho, com os dias parados à esquerda, e a
// página não rola de lado.
const props = defineProps({
  // As casas { dow, hour, … }; a que falta vale `empty`.
  cells: { type: Array, default: () => [] },
  empty: { type: Object, default: () => ({}) },
  // O valor de uma casa: null ou 0 é a casa vazia.
  valueOf: { type: Function, required: true },
  // O texto de uma casa, com o nome do dia: (cell, dayName) => string.
  cellText: { type: Function, required: true },
  // O prefixo do data-test-id das linhas e das casas.
  testId: { type: String, required: true },
});

const { weekdayOf } = useReportFormatter();
const tooltip = useHeatmapTooltip();

const cellsByDay = computed(() => {
  const byKey = new Map(
    props.cells.map(cell => [`${cell.dow}-${cell.hour}`, cell])
  );
  return Array.from({ length: 7 }, (_, dow) => ({
    dow,
    name: weekdayOf(dow),
    cells: Array.from(
      { length: 24 },
      (__, hour) => byKey.get(`${dow}-${hour}`) || { dow, hour, ...props.empty }
    ),
  }));
});

const max = computed(() =>
  Math.max(
    0,
    ...cellsByDay.value.flatMap(day =>
      day.cells.map(cell => props.valueOf(cell) || 0)
    )
  )
);

// Cinco tons, do valor sobre o maior do mapa. As classes vão por inteiro: o
// Tailwind só gera o que lê no fonte.
const LEVELS = [
  'bg-n-blue-3 border border-n-blue-4/30',
  'bg-n-blue-5 border border-n-blue-6/30',
  'bg-n-blue-7 border border-n-blue-8/30',
  'bg-n-blue-9 border border-n-blue-9/30',
  'bg-n-blue-11 border border-n-blue-10/30',
];
const EMPTY = 'border border-n-container bg-n-slate-2 dark:bg-n-slate-1/30';
const cellClass = cell => {
  const value = props.valueOf(cell);
  if (!value || !max.value) return EMPTY;
  const level = Math.min(Math.ceil((value / max.value) * 5), 5);
  return LEVELS[level - 1];
};
</script>

<template>
  <div
    class="grid relative items-start w-full gap-x-4 grid-cols-[80px_minmax(0,1fr)]"
  >
    <div class="grid gap-[5px] flex-shrink-0">
      <div
        v-for="day in cellsByDay"
        :key="day.dow"
        class="flex justify-end items-center h-8 text-[10px] font-semibold min-w-[70px] text-n-slate-12"
      >
        {{ day.name }}
      </div>
    </div>
    <div class="overflow-x-auto">
      <div class="flex flex-col gap-2.5 min-w-[700px]">
        <div class="grid gap-[5px] w-full">
          <div
            v-for="day in cellsByDay"
            :key="day.dow"
            :data-test-id="`${testId}-dia-${day.dow}`"
            class="grid gap-[5px] grid-cols-[repeat(24,_1fr)]"
          >
            <div
              v-for="cell in day.cells"
              :key="cell.hour"
              role="img"
              :aria-label="cellText(cell, day.name)"
              :data-test-id="`${testId}-${day.dow}-${cell.hour}`"
              class="h-8 rounded-sm"
              :class="cellClass(cell)"
              @mouseenter="tooltip.show($event, cellText(cell, day.name))"
              @mouseleave="tooltip.hide"
            />
          </div>
        </div>
        <div
          class="grid grid-cols-[repeat(24,_1fr)] gap-[5px] w-full text-[8px] font-semibold h-5 text-n-slate-12"
        >
          <div
            v-for="hour in 24"
            :key="hour"
            class="flex justify-center items-center"
          >
            {{ hour - 1 }}
          </div>
        </div>
      </div>
    </div>

    <HeatmapTooltip
      :visible="tooltip.visible.value"
      :x="tooltip.x.value"
      :y="tooltip.y.value"
      :text="tooltip.value.value || ''"
    />
  </div>
</template>
